import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { AttachmentInfo, CalEvent } from "../types";
import { GAP_ROW_MIN, displaySubject, isActive, type DaySegment } from "../domain/day";
import { fd, hm } from "../domain/time";
import { useEventAttachments } from "../hooks/useEventAttachments";
import { useMeetingThreads } from "../hooks/useMeetingThreads";
import { useRepeatLabels } from "../hooks/useRepeatLabels";
import type { MeetingThread } from "../services/meetingThread";
import { PlusIcon } from "../../../shared/components/icons";
import { badgesFor, type RowDetails } from "./eventBadges";
import styles from "./CalendarModule.module.css";

interface ScheduleListProps {
  readonly segs: readonly DaySegment[];
  readonly allDay: readonly CalEvent[];
  readonly clashing: ReadonlySet<string>;
  /** A meeting just moved or added: its row pulses, and is scrolled into view. */
  readonly flashId: string | null;
  /** Gap rows count free time from the start of the working day. */
  readonly workStartMin: number;
  readonly onOpenEvent: (eventId: string) => void;
  /** Start a new meeting at a free time (minutes of the day), or with no time yet (null). */
  readonly onBook: (startMin: number | null) => void;
  /** Start a new meeting on the time finder. */
  readonly onFind: () => void;
  /** A meeting's attachments, for naming them on its row. */
  readonly loadAttachments: (eventId: string) => Promise<readonly AttachmentInfo[]>;
  /** A series' repeat label ("Repeats weekly"), for its rows. */
  readonly loadRepeat: (seriesMasterId: string) => Promise<string | null>;
  /** Who sent the latest email about a meeting, for its "Mail ·" tag; asked only once its row is on screen. */
  readonly loadThread: (event: CalEvent) => Promise<MeetingThread | null>;
}

function Row({
  event,
  time,
  meta,
  clashing,
  details,
  flash,
  onOpenEvent,
  onVisible,
}: Readonly<{
  event: CalEvent;
  time: string;
  meta: string;
  clashing: ReadonlySet<string>;
  details: RowDetails;
  flash: boolean;
  onOpenEvent: (eventId: string) => void;
  onVisible: (eventId: string) => void;
}>) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (flash) ref.current?.scrollIntoView?.({ block: "nearest" });
  }, [flash]);
  // Lazy loading: the row reports the first time it scrolls into view (at once where that can't be observed).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      onVisible(event.id);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((x) => x.isIntersecting)) return;
      onVisible(event.id);
      observer.disconnect();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [event.id, onVisible]);
  const inactive = event.response === "no" || event.isCancelled;
  const tags = badgesFor(event, clashing, details);
  return (
    <button
      ref={ref}
      type="button"
      className={`${styles.listRow} ${inactive ? styles.listRowDeclined : ""} ${clashing.has(event.id) ? styles.listRowConflict : ""} ${
        flash ? styles.flash : ""
      }`}
      onClick={() => onOpenEvent(event.id)}
    >
      <span className={styles.listTime}>{time}</span>
      <span className={styles.listBody}>
        <span className={styles.listSubject}>{displaySubject(event)}</span>
        <span className={styles.listMeta}>{meta}</span>
        {tags.length > 0 && <span className={styles.listTags}>{tags}</span>}
      </span>
    </button>
  );
}

const organiserLabel = (e: CalEvent) => (e.isOrganizer ? "You" : e.organizerName);

/** The day as a list, with "N min free · Book" rows in the gaps. */
export function ScheduleList({
  segs,
  allDay,
  clashing,
  flashId,
  workStartMin,
  onOpenEvent,
  onBook,
  onFind,
  loadAttachments,
  loadRepeat,
  loadThread,
}: Readonly<ScheduleListProps>) {
  const events = [...allDay, ...segs.map((s) => s.event)];
  const attachments = useEventAttachments(loadAttachments, events);
  const repeats = useRepeatLabels(loadRepeat, events);
  // Email threads cost a mailbox search each, so only rows that have been on screen are searched.
  const [seen, setSeen] = useState<ReadonlySet<string>>(() => new Set());
  const markSeen = useCallback((id: string) => setSeen((s) => (s.has(id) ? s : new Set(s).add(id))), []);
  const threads = useMeetingThreads(loadThread, events.filter((e) => seen.has(e.id)));
  const detailsOf = (e: CalEvent): RowDetails => ({
    repeat: e.seriesMasterId ? repeats.get(e.seriesMasterId) : undefined,
    attachments: attachments.get(e.id),
    thread: threads.get(e.id)?.sender,
  });
  if (segs.length === 0 && allDay.length === 0) {
    return (
      <div className={styles.empty}>
        <b>Nothing booked</b>
        Your day is clear
        <div className={styles.emptyActions}>
          <button type="button" className={styles.btn} onClick={() => onBook(null)}>New Meeting</button>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onFind}>Find a Time</button>
        </div>
      </div>
    );
  }

  const rows: ReactNode[] = allDay.map((e) => (
    <Row
      key={e.id}
      event={e}
      time="All day"
      meta={[e.location, organiserLabel(e)].filter(Boolean).join(" · ")}
      clashing={clashing}
      details={detailsOf(e)}
      flash={flashId === e.id}
      onOpenEvent={onOpenEvent}
      onVisible={markSeen}
    />
  ));

  let prevEnd = workStartMin;
  for (const s of segs) {
    const e = s.event;
    const gap = s.startMin - prevEnd;
    if (gap >= GAP_ROW_MIN && isActive(e)) {
      const gapStart = prevEnd;
      rows.push(
        <div key={`gap-${e.id}`} className={styles.gapRow}>
          <span>{fd(gap)} free</span>
          <button type="button" className={styles.newLink} onClick={() => onBook(gapStart)}>
            <PlusIcon />
            Book {hm(prevEnd)}
          </button>
        </div>,
      );
    }
    rows.push(
      <Row
        key={e.id}
        event={e}
        time={hm(s.startMin)}
        meta={[e.location, fd(s.endMin - s.startMin), organiserLabel(e)].filter(Boolean).join(" · ")}
        clashing={clashing}
        details={detailsOf(e)}
        flash={flashId === e.id}
        onOpenEvent={onOpenEvent}
        onVisible={markSeen}
      />,
    );
    if (isActive(e)) prevEnd = Math.max(prevEnd, s.endMin);
  }

  return <>{rows}</>;
}
